import { useCallback, useState } from "react";

import { useHistory } from "react-router-dom";

import { saveTokenPair } from "apiCommandsExecutor/token";
import {
    requestSignInCode,
    signInWithCode,
    signInWithPassword,
} from "services/auth/rest";
import { TokenPairDto } from "services/auth/restModels";
import { isCompletePhone } from "shared/lib/phoneNumber";
import { useNotificationSnackbar } from "shared/ui";
import { useSession } from "shared/hooks/useSession";

export type SignInMethod = "sms" | "password";
export type SignInStep = "phone" | "code";

export const SIGN_IN_CODE_LENGTH = 4;

export const useSignInState = () => {
    const history = useHistory();
    const { account, refresh } = useSession();
    const notify = useNotificationSnackbar();

    const [method, setMethod] = useState<SignInMethod>("sms");
    const [step, setStep] = useState<SignInStep>("phone");
    const [phone, setPhone] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [code, setCode] = useState<string>("");
    const [submitting, setSubmitting] = useState<boolean>(false);

    const isPhoneComplete = isCompletePhone(phone);

    /*
    * После успешного входа перечитываем сессию и уводим на список меню сами: SecurityLayout
    * решает про доступ, но экран входа лежит вне его ветки роутов и редиректа не дождётся.
    *
    * ⚠️ Пустой ответ проверяем руками: strictNullChecks в проекте выключен, и TokenPairDto|null
    * компилятор пропустит молча, а saveTokenPair на пустом значении просто ничего не сделает —
    * пользователь остался бы на форме без единого сообщения.
    * */
    const completeSignIn = useCallback(
        async (tokenPair: TokenPairDto | null) => {
            if (!tokenPair) throw new Error("Не удалось получить токен авторизации");

            saveTokenPair(tokenPair);

            // ⚠️ refresh() не бросает, а отдаёт null и на сетевой ошибке, и на 401.
            if (!(await refresh())) {
                throw new Error("Вход выполнен, но не удалось загрузить данные аккаунта");
            }

            history.push("/menus/regular/list");
        },
        [history, refresh]
    );

    const changeMethod = useCallback((next: SignInMethod) => {
        setMethod(next);
        setStep("phone");
        setPassword("");
        setCode("");
    }, []);

    /*
    * Проверки готовности живут в самих действиях, а не только в `disabled` кнопки: то же
    * действие вызывает Enter в поле телефона, и на половине набранного номера запрос уходил бы
    * с гарантированным отказом.
    * */
    const sendCode = useCallback(async () => {
        if (!isPhoneComplete || submitting) return;

        setSubmitting(true);
        try {
            await requestSignInCode({ phoneNumber: phone });
            setCode("");
            setStep("code");
        } catch (e) {
            notify.showError("Не удалось отправить код", e);
        } finally {
            setSubmitting(false);
        }
    }, [isPhoneComplete, notify, phone, submitting]);

    /*
    * На успехе флаг не снимаем: completeSignIn уводит на другую страницу, экран размонтируется,
    * и setState в finally дал бы предупреждение React на каждый вход.
    * */
    const submitCode = useCallback(
        async (enteredCode: string) => {
            if (submitting) return;

            setSubmitting(true);
            try {
                await completeSignIn(
                    await signInWithCode({ phoneNumber: phone, code: enteredCode })
                );
            } catch (e) {
                setCode("");
                setSubmitting(false);
                notify.showError("Не удалось войти", e);
            }
        },
        [completeSignIn, notify, phone, submitting]
    );

    const submitPassword = useCallback(async () => {
        if (!isPhoneComplete || !password || submitting) return;

        setSubmitting(true);
        try {
            await completeSignIn(await signInWithPassword({ phoneNumber: phone, password }));
        } catch (e) {
            setSubmitting(false);
            notify.showError("Не удалось войти", e);
        }
    }, [completeSignIn, isPhoneComplete, notify, password, phone, submitting]);

    const backToPhone = useCallback(() => {
        setStep("phone");
        setCode("");
    }, []);

    return {
        isAuthorized: !!account,
        method,
        step,
        phone,
        password,
        code,
        submitting,
        isPhoneComplete,
        notify,
        setPhone,
        setPassword,
        setCode,
        changeMethod,
        sendCode,
        submitCode,
        submitPassword,
        backToPhone,
    };
};
